import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

function missingWidth(bar: HTMLElement): number {
  return textActions(bar).reduce((sum, b) => sum + Math.max(0, b.scrollWidth - b.clientWidth), 0)
}

function freeWidth(bar: HTMLElement): number {
  const left = bar.querySelector(':scope > .bar-left')?.getBoundingClientRect()
  const right = bar.querySelector(':scope > .bar-right')?.getBoundingClientRect()
  if (!left || !right) return 0
  const gap = parseFloat(getComputedStyle(bar).columnGap)
  const center = bar.querySelector<HTMLElement>(':scope > .bar-center')
  if (!center) return right.left - left.right - gap
  const children = [...center.children]
  const content = children.reduce((width, child) => width + child.getBoundingClientRect().width, 0)
    + Math.max(0, children.length - 1) * parseFloat(getComputedStyle(center).columnGap)
  return right.left - left.right - content - 2 * gap
}

function labelWidth(bar: HTMLElement): number {
  return textActions(bar).reduce(
    (sum, b) => sum + b.scrollWidth - parseFloat(getComputedStyle(b).getPropertyValue('--pill-segment-size')),
    0,
  )
}

const textActions = (bar: HTMLElement) => [...bar.querySelectorAll<HTMLElement>(':scope > .bar-right .pill > .btn:not(.btn-icon)')]

export function useCompactActions(bar: RefObject<HTMLElement | null>, actions: string): boolean {
  const [compactFor, setCompactFor] = useState<string | null>(null)
  if (compactFor !== null && compactFor !== actions) setCompactFor(null)
  const compact = compactFor === actions
  const labels = useRef(0)
  const fit = useRef(() => {})
  fit.current = () => {
    const el = bar.current
    if (!el?.isConnected) return
    if (!compact) {
      if (missingWidth(el) <= 0) return
      labels.current = labelWidth(el)
      setCompactFor(actions)
    } else if (freeWidth(el) - labels.current >= 1) setCompactFor(null)
  }
  useLayoutEffect(() => fit.current())
  useEffect(() => {
    const el = bar.current
    if (!el) return
    const refit = () => fit.current()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refit)
    observer?.observe(el)
    const left = el.querySelector(':scope > .bar-left')
    if (left) observer?.observe(left)
    const center = el.querySelector(':scope > .bar-center')
    if (center) observer?.observe(center)
    void document.fonts?.ready.then(refit)
    return () => observer?.disconnect()
  }, [bar])
  return compact
}
